import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import { Button } from './ui/button';
import { projects } from '../mock';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [displayedText, setDisplayedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const [isOnDarkBackground, setIsOnDarkBackground] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeSection, setActiveSection] = useState('hero');
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const fullText = 'Carol Mezher';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Detect active section on homepage
      if (location.pathname === '/') {
        const sections = ['hero', 'about', 'contact'];
        const scrollPosition = window.scrollY + 200; // Offset for header
        
        for (const sectionId of sections) {
          const section = document.getElementById(sectionId);
          if (section) {
            const sectionTop = section.offsetTop;
            const sectionBottom = sectionTop + section.offsetHeight;
            
            if (scrollPosition >= sectionTop && scrollPosition < sectionBottom) {
              setActiveSection(sectionId);
              break;
            }
          }
        }
      }
      
      // Detect background color at header position
      const headerHeight = 80; // Approximate header height
      const checkPoint = headerHeight + 10; // Check just below the header
      const element = document.elementFromPoint(window.innerWidth / 2, checkPoint);
      
      if (element) {
        const bgColor = window.getComputedStyle(element).backgroundColor;
        const parent = element.closest('section, div[class*="bg-"]');
        const parentBg = parent ? window.getComputedStyle(parent).backgroundColor : bgColor;
        
        // Check if background is dark (midnight-blue, burgundy, charcoal, or soft-cobalt panel)
        // midnight-blue: rgb(27, 31, 59), burgundy: rgb(135, 45, 55), soft-cobalt: rgb(122, 149, 201)
        const isDark = 
          parentBg.includes('27, 31, 59') ||    // midnight-blue
          parentBg.includes('107, 28, 59') ||   // legacy whale-blue
          parentBg.includes('102, 41, 54') ||   // legacy burgundy
          parentBg.includes('135, 45, 55') ||   // current burgundy
          parentBg.includes('168, 50, 74') ||   // legacy cherry
          parentBg.includes('126, 139, 163') || // legacy steel-blue
          parentBg.includes('143, 163, 209') || // legacy periwinkle
          parentBg.includes('122, 149, 201') || // legacy soft-cobalt
          parentBg.includes('159, 180, 212') || // legacy sky-steel
          parentBg.includes('129, 139, 177') || // current steel-blue panel
          parentBg.includes('26, 26, 26') ||    // charcoal
          element.classList.contains('bg-midnight-blue') ||
          element.classList.contains('bg-whale-blue') ||
          element.classList.contains('bg-terracotta') ||
          parent?.classList.contains('bg-midnight-blue') ||
          parent?.classList.contains('bg-whale-blue') ||
          parent?.classList.contains('bg-terracotta');
        
        setIsOnDarkBackground(isDark);
      }
    };
    
    handleScroll(); // Check on mount
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]);

  // Looping typing animation effect
  useEffect(() => {
    let currentIndex = 0;
    let isDeleting = false;
    let pauseUntil = 0; // ms timestamp; pause ticks until this time

    const typeLoop = () => {
      if (Date.now() < pauseUntil) return;

      if (!isDeleting) {
        if (currentIndex < fullText.length) {
          currentIndex++;
          setDisplayedText(fullText.slice(0, currentIndex));
          setShowCursor(true);
        } else {
          // Full name typed — hold for 2s, fade cursor, hold 2s more, then delete
          setTimeout(() => setShowCursor(false), 2000);
          pauseUntil = Date.now() + 4000;
          isDeleting = true;
        }
      } else {
        if (currentIndex > 0) {
          currentIndex--;
          setDisplayedText(fullText.slice(0, currentIndex));
          setShowCursor(true);
        } else {
          // Fully erased — pause 1s before retyping
          isDeleting = false;
          pauseUntil = Date.now() + 1000;
        }
      }
    };

    const interval = setInterval(typeLoop, 150);
    return () => clearInterval(interval);
  }, []);

  const handleNavigation = (e, path, sectionId) => {
    setIsMobileMenuOpen(false);

    if (path === '/projects' || path === '/collaborations' || path === '/services' || path === '/about') {
      // Let Link navigate to dedicated page — no extra work
      return;
    }

    // path === '/' (homepage section link, e.g. Contact)
    if (location.pathname === '/') {
      // Already on homepage, prevent navigation and just scroll
      e.preventDefault();
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // Let Link navigate to /, then scroll once route changes
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const results = projects.filter(project => 
        project.title.toLowerCase().includes(query) ||
        project.subtitle?.toLowerCase().includes(query) ||
        project.description?.toLowerCase().includes(query) ||
        project.category?.toLowerCase().includes(query)
      ).slice(0, 5); // Limit to 5 results
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
        setSearchOpen(false);
        setSearchQuery('');
        setSearchResults([]);
      }
    };

    if (searchOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchOpen]);

  // Focus input when search opens
  useEffect(() => {
    if (searchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchOpen]);

  const handleSearchResultClick = (project) => {
    // Determine the category route
    const categoryRoutes = {
      'Films & TV Series': 'film-tv',
      'Industry & Cultural Programming': 'film-festivals',
      'Theatrical Shows & Immersive Experiences': 'theatrical-shows',
      'Exhibitions & Immersive Experiences': 'exhibitions',
      'Sports Ceremonies': 'sports-ceremonies',
      'National Celebrations': 'national-celebrations'
    };
    
    const categoryRoute = categoryRoutes[project.category] || 'film-tv';
    navigate(`/projects/${categoryRoute}?project=${project.id}`);
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const navItems = [
    // 'Home' replaces the now-removed page-level "Back to Home" buttons.
    // path='/' + id='hero' makes handleNavigation either scroll to the hero
    // (when already on /) or route to / and then scroll, mirroring how the
    // Carol Mezher logo behaves.
    { label: 'Home', id: 'hero', path: '/' },
    { label: 'About', id: 'about', path: '/about' },
    { label: 'Services', id: 'services', path: '/services' },
    { label: 'Portfolio', id: 'projects', path: '/projects' },
    { label: 'Collaborations', id: 'collaborations', path: '/collaborations' },
    { label: 'Contact', id: 'contact', path: '/' }
  ];

  // Desktop nav always reads on a midnight-blue header, so links stay
  // white at all scroll positions. Mobile and the scrolled state still
  // use burgundy (via the un-prefixed text-whale-blue).
  const linkInactiveColor =
    'text-whale-blue hover:text-terracotta lg:text-white lg:hover:text-terracotta';
  const linkActiveColor = 'text-whale-blue lg:text-white';
  const activeUnderlineColor = 'bg-whale-blue lg:bg-terracotta';

  return (
    <header
      // Desktop: always transparent so the midnight-blue + pattern
      // backdrop below shows through — same chrome whether the user
      // is at the top of the page or has scrolled.
      // Mobile: keeps the existing cream-at-top, white-on-scroll
      // behaviour so the existing mobile design is untouched.
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 lg:bg-transparent lg:backdrop-blur-none lg:shadow-none ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-sm shadow-md'
          : 'bg-cream/95 backdrop-blur-sm'
      }`}
    >
      {/* Desktop-only midnight-blue + diamond pattern backdrop. Renders at
          all scroll positions so the desktop header keeps the same colour
          rather than flipping to white when the user scrolls.
          Height fixed at 72px (same value Hero.jsx now uses for its
          pattern container offset) so the backdrop reaches exactly to
          where the Hero's pattern begins, with no flat band in between. */}
      <div
        aria-hidden="true"
        className="hidden lg:block absolute left-0 right-0 top-0 h-[72px] -z-10 overflow-hidden bg-midnight-blue"
      >
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage: 'url(/hero-pattern.png)',
            backgroundRepeat: 'repeat',
            backgroundSize: '760px auto',
            // Place tile-row-0 at the bottom of the backdrop (y=72) so the
            // hero's tile-row-0 at y=72 lines up — seamless continuation.
            backgroundPosition: '0 72px',
          }}
        />
      </div>

      <div className="container mx-auto px-8 md:px-16 py-5">
        <div className="flex items-center justify-between">
          <Link
            to="/"
            onClick={(e) => {
              if (location.pathname === '/') {
                e.preventDefault();
              }
              setTimeout(() => scrollToSection('hero'), 100);
            }}
            className="font-playfair font-bold text-2xl text-terracotta hover:text-whale-blue transition-colors"
          >
            {displayedText}
            {showCursor && <span className="animate-pulse ml-0.5">|</span>}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-10">
            {navItems.map((item) => {
              // Determine if this nav item is active
              let isActive = false;
              
              if (item.path === '/' && location.pathname === '/') {
                // For homepage sections (About, Contact), check active section
                isActive = activeSection === item.id;
              } else {
                // For other pages (Services, Portfolio, Collaborations)
                isActive = location.pathname === item.path;
              }
              
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  onClick={(e) => handleNavigation(e, item.path, item.id)}
                  className={`text-base tracking-wide font-montserrat font-bold transition-all relative group px-3 py-1 rounded ${
                    // Burgundy on the light header (mobile / scrolled),
                    // white on the dark hero-matched header (desktop, top).
                    isActive ? linkActiveColor : linkInactiveColor
                  }`}
                >
                  {item.label}
                  <span className={`absolute bottom-0 left-0 h-0.5 transition-all duration-300 ${
                    isActive
                      ? `w-full ${activeUnderlineColor}`
                      : 'w-0 group-hover:w-full bg-terracotta'
                  }`}></span>
                </Link>
              );
            })}
            
            {/* Search with expandable input */}
            <div ref={searchContainerRef} className="relative">
              <div className="flex items-center">
                {searchOpen && (
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search projects..."
                    className={`w-64 px-4 py-2 rounded-full border-2 transition-all mr-2 font-montserrat text-sm ${
                      isOnDarkBackground 
                        ? 'bg-white/10 border-terracotta text-white placeholder-white/60 focus:bg-white/20' 
                        : 'bg-white border-whale-blue text-charcoal placeholder-charcoal/50 focus:border-terracotta'
                    } focus:outline-none`}
                  />
                )}
                <button
                  onClick={() => setSearchOpen(!searchOpen)}
                  onMouseEnter={() => setSearchOpen(true)}
                  className={`p-2 rounded-full transition-all hover:bg-opacity-10 ${
                    isOnDarkBackground 
                      ? 'text-terracotta hover:bg-white' 
                      : 'text-whale-blue hover:bg-whale-blue'
                  }`}
                  aria-label="Search"
                >
                  <Search size={20} />
                </button>
              </div>

              {/* Search Results Dropdown */}
              {searchOpen && searchResults.length > 0 && (
                <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border border-charcoal/10 max-h-96 overflow-y-auto z-50">
                  {searchResults.map((project) => (
                    <button
                      key={project.id}
                      onClick={() => handleSearchResultClick(project)}
                      className="w-full text-left p-4 hover:bg-whale-blue/7 transition-colors border-b border-charcoal/5 last:border-b-0"
                    >
                      <h4 className="font-playfair font-bold text-base text-charcoal mb-1">
                        {project.title}
                      </h4>
                      <p className="text-xs text-whale-blue font-montserrat font-semibold mb-1">
                        {project.category}
                      </p>
                      {project.subtitle && (
                        <p className="text-sm text-charcoal/70 line-clamp-1">
                          {project.subtitle}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* No results message */}
              {searchOpen && searchQuery.trim() && searchResults.length === 0 && (
                <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border border-charcoal/10 p-4 z-50">
                  <p className="text-charcoal/70 text-sm font-montserrat">
                    No projects found for "{searchQuery}"
                  </p>
                </div>
              )}
            </div>
          </nav>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden text-terracotta hover:text-whale-blue"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="md:hidden mt-6 -mx-8 px-8 pt-6 pb-6 space-y-4 border-t border-white/10 bg-midnight-blue">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                onClick={(e) => handleNavigation(e, item.path, item.id)}
                className="block w-full text-left text-base font-montserrat font-medium text-white hover:text-terracotta transition-colors py-2"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
